// Minimal E2EE client utilities using WebCrypto
// NOTE: This is a lightweight initial implementation and should be reviewed by a security expert before production use.

export async function generateECDHKeyPair() {
  const keyPair = await window.crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveKey', 'deriveBits']
  );
  return keyPair;
}

export async function exportPublicKeyBase64(publicKey) {
  const spki = await window.crypto.subtle.exportKey('spki', publicKey);
  const b64 = arrayBufferToBase64(spki);
  return b64;
}

export async function exportPrivateKeyJwk(privateKey) {
  const jwk = await window.crypto.subtle.exportKey('jwk', privateKey);
  return jwk;
}

export function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export function base64ToArrayBuffer(base64) {
  const binary = window.atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// -------------------------
// IndexedDB small wrapper
// -------------------------
function openDB() {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open('mastodon-e2ee', 1);
    req.onupgradeneeded = function (ev) {
      const db = ev.target.result;
      if (!db.objectStoreNames.contains('private_keys')) {
        db.createObjectStore('private_keys');
      }
    };
    req.onsuccess = function () { resolve(req.result); };
    req.onerror = function () { reject(req.error); };
  });
}

async function idbPut(key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('private_keys', 'readwrite');
    const store = tx.objectStore('private_keys');
    const req = store.put(value, key);
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('private_keys', 'readonly');
    const store = tx.objectStore('private_keys');
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// -------------------------
// PBKDF2 + AES-GCM helpers
// -------------------------
async function deriveKey(passphrase, salt, iterations = 200_000) {
  const enc = new TextEncoder();
  const passKey = await window.crypto.subtle.importKey('raw', enc.encode(passphrase), { name: 'PBKDF2' }, false, ['deriveKey']);
  return window.crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
    passKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function randomBytes(len) {
  const b = new Uint8Array(len);
  window.crypto.getRandomValues(b);
  return b;
}

export async function encryptJwkWithPassphrase(jwk, passphrase) {
  const salt = randomBytes(16);
  const iv = randomBytes(12);
  const key = await deriveKey(passphrase, salt);
  const enc = new TextEncoder();
  const data = enc.encode(JSON.stringify(jwk));
  const ct = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
  return {
    ciphertext: arrayBufferToBase64(ct),
    iv: arrayBufferToBase64(iv.buffer),
    salt: arrayBufferToBase64(salt.buffer)
  };
}

export async function decryptJwkWithPassphrase({ ciphertext, iv, salt }, passphrase) {
  const ct = base64ToArrayBuffer(ciphertext);
  const ivBuf = base64ToArrayBuffer(iv);
  const saltBuf = base64ToArrayBuffer(salt);
  const key = await deriveKey(passphrase, saltBuf);
  const decrypted = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv: ivBuf }, key, ct);
  const dec = new TextDecoder();
  return JSON.parse(dec.decode(decrypted));
}

// -------------------------
// Storage helpers
// -------------------------
export async function storePrivateKeyLocally(jwk, { passphrase } = {}) {
  if (passphrase) {
    const encrypted = await encryptJwkWithPassphrase(jwk, passphrase);
    await idbPut('private_key_encrypted', encrypted);
    return { stored: 'indexeddb', encrypted: true };
  }

  // Backward-compat: store as JSON in localStorage (not recommended)
  localStorage.setItem('e2ee_private_key', JSON.stringify(jwk));
  return { stored: 'localstorage', encrypted: false };
}

export async function getStoredPrivateKeyEncrypted() {
  return await idbGet('private_key_encrypted');
}

export async function getStoredPrivateKey(passphrase) {
  const enc = await getStoredPrivateKeyEncrypted();
  if (!enc) {
    const raw = localStorage.getItem('e2ee_private_key');
    if (!raw) return null;
    return JSON.parse(raw);
  }
  if (!passphrase) throw new Error('Passphrase required to decrypt stored private key');
  return await decryptJwkWithPassphrase(enc, passphrase);
}

export async function exportPrivateKeyEncrypted(passphrase, filename = 'mastodon_e2ee_private_key.json') {
  const enc = await getStoredPrivateKeyEncrypted();
  if (!enc) throw new Error('No encrypted private key stored');
  const payload = { encrypted_key: enc };
  const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function importPrivateKeyEncrypted(file, passphrase) {
  const text = await file.text();
  const parsed = JSON.parse(text);
  if (!parsed.encrypted_key) throw new Error('Invalid key file');
  // Try to decrypt to validate passphrase
  await decryptJwkWithPassphrase(parsed.encrypted_key, passphrase);
  await idbPut('private_key_encrypted', parsed.encrypted_key);
  return true;
}

// -------------------------
// Existing helpers
// -------------------------
function getCsrfToken() {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('meta[name="csrf-token"]');
  return el && el.getAttribute('content');
}

export async function uploadPublicKey(accountId, publicKeyBase64) {
  const headers = { 'Content-Type': 'application/json' };
  const csrf = getCsrfToken();
  if (csrf) headers['X-CSRF-Token'] = csrf;

  const resp = await fetch(`/api/v1/accounts/${accountId}/e2ee_key`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ public_key: publicKeyBase64 }),
    credentials: 'same-origin'
  });

  if (!resp.ok) {
    let bodyText;
    try {
      const json = await resp.json();
      bodyText = json.error || JSON.stringify(json);
    } catch (e) {
      try { bodyText = await resp.text(); } catch (_) { bodyText = '' }
    }
    const reqId = resp.headers.get && resp.headers.get('x-request-id');
    throw new Error(`Upload failed: ${resp.status} ${bodyText}${reqId ? ' (x-request-id: ' + reqId + ')' : ''}`);
  }

  return await resp.json();
}

export async function generateAndUploadKeypair(accountId, { passphrase } = {}) {
  // Prefer libsodium X25519 keypairs when available
  try {
    const sodium = await initSodium();
    const kp = sodium.crypto_box_keypair();
    const pub = arrayBufferToBase64(kp.publicKey);
    const priv = arrayBufferToBase64(kp.privateKey);
    // Store as JWK-like structure to keep compatibility with other flows
    const jwk = { kty: 'OKP', crv: 'X25519', x: pub, d: priv };
    await storePrivateKeyLocally(jwk, { passphrase });
    await uploadPublicKey(accountId, pub);
    return { publicKey: pub, privateJwk: jwk };
  } catch (e) {
    // Fallback to ECDH P-256 (existing code)
    const kp = await generateECDHKeyPair();
    const pub = await exportPublicKeyBase64(kp.publicKey);
    const jwk = await exportPrivateKeyJwk(kp.privateKey);
    await storePrivateKeyLocally(jwk, { passphrase });
    await uploadPublicKey(accountId, pub);
    return { publicKey: pub, privateJwk: jwk };
  }
}

// -------------------------
// Libsodium helpers (X25519 + sealed boxes + secretbox)
// -------------------------
let _sodium = null;
export async function initSodium() {
  if (_sodium) return _sodium;
  if (!window || !window.document) throw new Error('Not in browser environment');
  // dynamic import so that node/env without libsodium won't fail
  const sodium = await import('libsodium-wrappers');
  await sodium.ready;
  _sodium = sodium;
  return _sodium;
}

export async function generateSodiumKeypair() {
  const sodium = await initSodium();
  const kp = sodium.crypto_box_keypair();
  return {
    publicKey: sodium.to_base64(kp.publicKey, sodium.base64_variants.ORIGINAL),
    privateKey: sodium.to_base64(kp.privateKey, sodium.base64_variants.ORIGINAL)
  };
}

export async function encryptConvKeyForRecipient(convKeyBase64, recipientPublicKeyBase64) {
  // Use crypto_box_seal to anonymously encrypt convKey for recipient
  const sodium = await initSodium();
  const convKey = base64ToArrayBuffer(convKeyBase64);
  const recipientPub = sodium.from_base64(recipientPublicKeyBase64, sodium.base64_variants.ORIGINAL);
  const sealed = sodium.crypto_box_seal(convKey, recipientPub);
  return sodium.to_base64(sealed, sodium.base64_variants.ORIGINAL);
}

export async function decryptSealedConvKey(sealedBase64, recipientPrivateKeyBase64, recipientPublicKeyBase64) {
  const sodium = await initSodium();
  const sealed = sodium.from_base64(sealedBase64, sodium.base64_variants.ORIGINAL);
  const sk = sodium.from_base64(recipientPrivateKeyBase64, sodium.base64_variants.ORIGINAL);
  const pk = sodium.from_base64(recipientPublicKeyBase64, sodium.base64_variants.ORIGINAL);
  const decrypted = sodium.crypto_box_seal_open(sealed, pk, sk);
  return arrayBufferToBase64(decrypted.buffer);
}

export function generateConversationKey() {
  // 32 bytes random key (256-bit)
  const k = randomBytes(32);
  return arrayBufferToBase64(k.buffer);
}

export async function encryptMessageWithConvKey(message, convKeyBase64) {
  const sodium = await initSodium();
  const key = sodium.from_base64(convKeyBase64, sodium.base64_variants.ORIGINAL);
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const msgBuf = new TextEncoder().encode(message);
  const cipher = sodium.crypto_secretbox_easy(msgBuf, nonce, key);
  // return base64 concatenation of nonce + cipher
  const out = new Uint8Array(nonce.length + cipher.length);
  out.set(nonce, 0);
  out.set(cipher, nonce.length);
  return sodium.to_base64(out, sodium.base64_variants.ORIGINAL);
}

export async function decryptMessageWithConvKey(cipherBase64, convKeyBase64) {
  const sodium = await initSodium();
  const out = sodium.from_base64(cipherBase64, sodium.base64_variants.ORIGINAL);
  const nonce = out.slice(0, sodium.crypto_secretbox_NONCEBYTES);
  const cipher = out.slice(sodium.crypto_secretbox_NONCEBYTES);
  const key = sodium.from_base64(convKeyBase64, sodium.base64_variants.ORIGINAL);
  const plain = sodium.crypto_secretbox_open_easy(cipher, nonce, key);
  return new TextDecoder().decode(plain);
}

export async function distributeConversationKey(conversationId, convKeyBase64, participants) {
  // participants: array of { accountId, publicKeyBase64 }
  for (const p of participants) {
    const encrypted = await encryptConvKeyForRecipient(convKeyBase64, p.publicKeyBase64);
    // POST to server
    const csrf = getCsrfToken();
    const headers = { 'Content-Type': 'application/json' };
    if (csrf) headers['X-CSRF-Token'] = csrf;

    const resp = await fetch(`/api/v1/conversations/${conversationId}/keys`, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify({ recipient_account_id: p.accountId, encrypted_key: encrypted })
    });
    if (!resp.ok) {
      throw new Error('Failed to store encrypted conversation key for recipient ' + p.accountId);
    }
  }
}

// Duplicate upload/generate functions removed — the implementations above (with libsodium support and passphrase option) are used.

// -------------------------
// Conversation / payload helpers
// -------------------------
export async function fetchConversationEncryptedKey(conversationId) {
  const resp = await fetch(`/api/v1/conversations/${conversationId}/keys`, {
    credentials: 'same-origin'
  });
  if (!resp.ok) throw new Error('Failed to fetch conversation key');
  const json = await resp.json();
  return json.encrypted_key;
}

export async function decryptConversationKeyForMe(conversationId, { passphrase } = {}) {
  const sealed = await fetchConversationEncryptedKey(conversationId);
  if (!sealed) throw new Error('No encrypted conversation key available for you');
  // load private key (throws if passphrase required)
  const jwk = await getStoredPrivateKey(passphrase);
  const priv = jwk.d;
  const pub = jwk.x;
  if (!priv || !pub) throw new Error('Invalid private key stored locally');
  return await decryptSealedConvKey(sealed, priv, pub);
}

export async function decryptStatusPayload(conversationId, cipherBase64, passphrase) {
  const convKeyBase64 = await decryptConversationKeyForMe(conversationId, { passphrase });
  return await decryptMessageWithConvKey(cipherBase64, convKeyBase64);
}
